import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';

import { useFetchSmartLinkData } from '../../../../../../../common/controller/use-fetch-smartlink-data/useFetchSmartLinkData';
import { AppIcon } from '../../../../../../../common/ui/app-icon/app-icon';
import { IconStack } from '../../../../../../../common/ui/sources-stack/icon-stack';
import { messages } from '../../messages';

const DEFAULT_MAX_SOURCES_IN_STACK = 3;

const styles = cssMap({
	pressable: {
		backgroundColor: 'transparent',
	},
});

export const LinkIconStack = ({
	sources,
	onClick,
	maxSourcesInStack,
	singleColumnLayout = false,
}: {
	sources: string[];
	maxSourcesInStack?: number;
	onClick: (event: React.MouseEvent<Element, MouseEvent>) => void;
	singleColumnLayout?: boolean;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { objectResolverData: resolvedLinks, isLoading: objectResolverLoading } =
		useFetchSmartLinkData({ urls: sources });
	const sourcesForIconStack: string[] = [];
	const sourceNamesMap: Record<string, boolean> = {};
	maxSourcesInStack = maxSourcesInStack ?? DEFAULT_MAX_SOURCES_IN_STACK;

	for (let i = 0; i < resolvedLinks.length; i++) {
		const source = resolvedLinks[i];

		if (source.generator?.name && !sourceNamesMap[source.generator?.name]) {
			sourcesForIconStack.push(source.generator.name);
			sourceNamesMap[source.generator.name] = true;
		}

		if (sourcesForIconStack.length === maxSourcesInStack) {
			break;
		}
	}

	const remainingSourcesCount = singleColumnLayout
		? sources.slice(1).length
		: sources.slice(2).length;

	if (objectResolverLoading) {
		return (
			<Skeleton
				height="20px"
				width="100px"
				isShimmering
				testId="contributors-avatar-stack-skeleton"
			/>
		);
	}

	return (
		<Pressable onClick={onClick} xcss={styles.pressable}>
			<Inline alignBlock="center" space="space.050">
				<IconStack>
					{sourcesForIconStack.map((source, index) => (
						<AppIcon app={source} key={index} size="xxsmall" fallbackIcon={<GlobeIcon label={formatMessage(messages.globe_icon_label)} size="medium" />} />
					))}
				</IconStack>
				<Text color="color.text.subtle">
					<FormattedMessage
						{...messages.sources_icon_stack_text}
						values={{ count: remainingSourcesCount }}
					/>
				</Text>
			</Inline>
		</Pressable>
	);
};
